import { InfoRounded } from "@mui/icons-material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";
import { Box, Divider, Typography } from "@mui/material";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import {
  default as CustomToolTip,
  default as CustomTooltip,
} from "../../../../../components/CustomTooltip";
import LabelComponentProp from "../../../../../components/LabelComponentProp";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { AssetClass, PageDisplayMode } from "../../../../../utils/constants";
import { formatNumberCompact } from "../../../../../utils/formatters";
import { getPrettyDate, useLocalTime } from "../../../../../utils/timestamp";
import CoverageScoreLabel from "../../../../dashboard/mssp_widgets/stack_utilization_coverage/CoverageScoreLabel";
import { icons, iconsAsset } from "../../../environment/icons/iconExport";

const ContentAssetCard = ({
  asset,
  assetId = null,
  activeAsset = true,
  assetType,
  assetName,
  numEntityIds,
  scenarioSetsCount = null,
  displayMode,
  children,
}) => {
  const IconComponent = iconsAsset[assetType]
    ? iconsAsset[assetType]
    : icons.DefaultAssetIcon;
  const colors = useColors();
  const showLocalTime = useLocalTime();
  const navigate = useNavigate();
  const { trackEvent } = useAPI();
  const { businessValueMode } = useContext(EnvFeatureFlagContext);

  const handleScenarioClick = (event) => {
    if (scenarioSetsCount > 0 && activeAsset && assetId) {
      event.stopPropagation();
      trackEvent(`User clicked on attack scenarios for asset ${assetId}`);
      navigate("/posture/surface/", {
        state: { filterCriticalAsset: assetId },
      });
    }
  };

  const RegularAssetTooltip = () => {
    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Typography variant="overline" sx={{ fontSize: "11px" }}>
          {asset?.created_by && asset?.created_at
            ? `Created by ${asset?.created_by} on ${getPrettyDate(
                asset?.created_at?.timestamp_utc,
                showLocalTime
              )}. `
            : ""}
        </Typography>
        <Typography variant="overline" sx={{ fontSize: "11px" }}>
          {asset?.reason ? `\n\nDescription: ${asset?.reason}` : ""}
        </Typography>
      </Box>
    );
  };
  let regularAssetTooltip =
    (asset?.created_by && asset?.created_at
      ? `Created by ${asset?.created_by} on ${getPrettyDate(
          asset?.created_at?.timestamp_utc,
          showLocalTime
        )}. `
      : "") + (asset?.reason ? `\n\nDescription: ${asset?.reason}` : "");

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={displayMode === PageDisplayMode.CARD ? "16px" : "32px"}
      justifyContent={"space-between"}
      width={"100%"}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        gap={1}
        alignItems="center"
        justifyContent={"space-between"}
      >
        <Box display={"flex"} flexDirection={"row"} gap={1} alignItems="center">
          {IconComponent && <SvgToIcon IconLogo={IconComponent} width="24px" />}
          <Typography color={colors.text_primary} variant="subtitle2">
            {assetName}
          </Typography>
          {asset?.cls === AssetClass.SMART ? (
            <CustomToolTip title={asset?.reason} placement="top">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.primary_4percent,
                    boxShadow: "0 0 1px 0 " + colors.primary_48percent,
                    borderRadius: "6px",
                    padding: "0px 4px",
                    gap: "5px",
                    height: "18px",
                    color: colors.primary_main,
                  }}
                >
                  <AutoAwesomeRoundedIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Box>
              </Box>
            </CustomToolTip>
          ) : (
            regularAssetTooltip !== "" && (
              <CustomToolTip title={<RegularAssetTooltip />} placement="top">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <InfoRounded
                    sx={{
                      color: colors.grey_48percent,
                      width: "16px",
                      height: "16px",
                    }}
                  />
                </Box>
              </CustomToolTip>
            )
          )}
        </Box>
        {
          <CustomTooltip
            title={
              asset?.coverage
                ? ""
                : "There are no attack paths threatening this critical asset"
            }
            placement="top"
          >
            <Box>
              <CoverageScoreLabel
                value={asset?.coverage ? Math.round(asset?.coverage) : null}
                sx={{ borderRadius: "10px", height: "20px" }}
              />
            </Box>
          </CustomTooltip>
        }
      </Box>
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={
            businessValueMode && displayMode === PageDisplayMode.TABLE ? 1 : 2
          }
        >
          <LabelComponentProp
            label="Entities"
            textTypography="caption"
            alignItems="flex-start"
            sx={{
              minHeight: "24px",
              gap: "8px",
              color: colors.text_secondary,
            }}
          >
            <Typography variant="body2">{numEntityIds}</Typography>
          </LabelComponentProp>
          <Divider orientation="vertical" flexItem />
          <LabelComponentProp
            label="Attack Scenarios"
            textTypography="caption"
            alignItems="flex-start"
            sx={{
              minHeight: "24px",
              gap: "8px",
              color: colors.text_secondary,
            }}
          >
            <CustomToolTip
              title={scenarioSetsCount > 0 ? "View attack scenarios" : ""}
              placement="top"
            >
              <Box
                onClick={(event) => {
                  handleScenarioClick(event);
                }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.text_primary,
                    cursor: "pointer",
                  }}
                >
                  {scenarioSetsCount}
                </Typography>
                {scenarioSetsCount > 0 && (
                  <EastRoundedIcon
                    sx={{
                      color: colors.action_active,
                      width: "16px",
                      height: "16px",
                    }}
                  />
                )}
              </Box>
            </CustomToolTip>
          </LabelComponentProp>
          {businessValueMode && (
            <>
              <Divider orientation="vertical" flexItem />
              <LabelComponentProp
                label="Business value"
                textTypography="caption"
                alignItems="flex-start"
                sx={{
                  minHeight: "24px",
                  gap: "8px",
                  color: colors.text_secondary,
                }}
              >
                <Typography variant="body2">
                  ${formatNumberCompact(asset?.business_value || 0)}
                </Typography>
              </LabelComponentProp>
            </>
          )}
        </Box>{" "}
        {children}
      </Box>
    </Box>
  );
};

export default ContentAssetCard;
